import React from 'react';

import useUI from 'hooks/use-ui';
import { useCommerceComponents } from 'src/theme';
import type { CommerceComponents } from 'src/theme/provider/commerce-components/context';
import { useBuilderComponent, BuilderModelsRegistry } from 'src/contexts/builder';

/* eslint-disable @typescript-eslint/naming-convention */
export enum RemoteComponentSlot {
  Header = 'header',
  Navigation = 'navigation',
  Footer = 'footer',

  Hero = 'hero',
  ProductDetails = 'product-details',
  ListPageHero = 'list-page-hero',
}
/* eslint-enable @typescript-eslint/naming-convention */

type RemoteComponentOptions<P extends object> = {
  fallback?: React.FC<P>;
};

type CommerceComponentSlot = keyof CommerceComponents['components'];

function remoteComponentSlotToCommerceSlot(slot: RemoteComponentSlot): CommerceComponentSlot | null {
  switch (slot) {
    case RemoteComponentSlot.Header:
      return 'StoreFrontHeader';
    case RemoteComponentSlot.Navigation:
      return 'StoreFrontNavigation';
    case RemoteComponentSlot.Footer:
      return 'StoreFrontFooter';
    case RemoteComponentSlot.ProductDetails:
      return 'ProductDetailsPrimary';
    case RemoteComponentSlot.Hero:
      return 'StoreFrontHero';
    case RemoteComponentSlot.ListPageHero:
      return 'ListPageHero';
  }
  return null;
}

function remoteComponentSlotToBuilderSlot(slot: RemoteComponentSlot): BuilderModelsRegistry {
  switch (slot) {
    case RemoteComponentSlot.Header:
      return BuilderModelsRegistry.siteHeader;
    case RemoteComponentSlot.Footer:
      return BuilderModelsRegistry.siteFooter;
    case RemoteComponentSlot.Hero:
      return BuilderModelsRegistry.homePageHero;

    // no builder entry for these
    case RemoteComponentSlot.Navigation:
    case RemoteComponentSlot.ProductDetails:
    case RemoteComponentSlot.ListPageHero:
      return BuilderModelsRegistry.invalid;
  }
  return BuilderModelsRegistry.invalid;
}

function EmptyComponent(): JSX.Element {
  return <></>;
}

export function useRemoteComponent<T extends object>(
  slot: RemoteComponentSlot,
  options: RemoteComponentOptions<T> = {}
): React.ComponentType<T & { fallback?: React.ComponentType<T> }> {
  const { components } = useCommerceComponents();
  const { isStoreFront, isKiosk, isDutchieMain } = useUI();

  // options and fallback are both optional, options defaults to {} so we just need to default fallback here
  let { fallback }: { fallback?: React.ComponentType } = options;
  if (!fallback) {
    fallback = EmptyComponent;
  }

  // with a valid fallback, we just cascade the component types here
  // builder takes priority over ecommerce custom components
  // so we do the ecommerce one first so that it can be the fallback for builder
  const commerceSlot = remoteComponentSlotToCommerceSlot(slot);
  const CommerceComponent: React.ComponentType<T> =
    commerceSlot && (isStoreFront || isKiosk) ? components[commerceSlot]({ fallback }) : fallback;

  const builderSlot = remoteComponentSlotToBuilderSlot(slot);
  const ResolvedComponent = useBuilderComponent({
    model: builderSlot,
    skip: !builderSlot || isDutchieMain,
    fallback: CommerceComponent,
    // fetchOptions,
  });

  return ResolvedComponent.component;
}
